import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Monitor, Car, Heart, Users, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const StrabaneHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Strabane | Online Sessions for Border Community</title>
        <meta
          name="description"
          content="Professional hypnotherapy for Strabane and border communities. Online sessions available - no travel required. Anxiety, stress, chronic pain, accessible care. CNHC registered. Supporting working-class accessibility."
        />
        <meta name="keywords" content="hypnotherapy Strabane, hypnotherapist Strabane, online hypnotherapy Strabane, anxiety treatment Strabane, stress management Strabane, chronic pain Strabane, affordable therapy Strabane, therapist Strabane" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/strabane-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Strabane | Online Sessions for Border Community" />
        <meta property="og:description" content="Professional hypnotherapy for Strabane and border communities. Online sessions available - no travel required. Anxiety, stress, chronic pain. Accessible care." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Strabane",
            "description": "CNHC registered hypnotherapy serving Strabane and border communities via online sessions. Specializing in anxiety, stress management, chronic pain, and accessible mental health care. Supporting working-class communities with respectful, non-judgmental support.",
            "url": "https://www.sharonclare-hypnotherapy.com/strabane-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Strabane, County Tyrone",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Anxiety Treatment",
              "Stress Management",
              "Chronic Pain Management",
              "Economic Stress Support",
              "Accessible Mental Health Care"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy for Strabane
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Online sessions available for Strabane and border community residents
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Monitor className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700 font-semibold">
                  Online sessions available - no travel required
                </p>
              </div>
              <p className="text-gray-600">
                <span className="font-semibold">We strongly recommend online sessions</span> as the practical option for Strabane residents. Online hypnotherapy is just as effective while eliminating lengthy travel.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Strabane and Border Communities
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                While I don't have a physical clinic in Strabane, I'm proud to serve the border community through secure online hypnotherapy sessions. As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand that geography shouldn't be a barrier to accessing quality mental health support. Traveling long distances for regular in-person sessions simply isn't practical for most Strabane residents. That's why online sessions are the ideal solution.
              </p>
              <p>
                Online hypnotherapy via secure video call provides exactly the same professional, evidence-based treatment as face-to-face sessions, with the crucial advantage of eliminating travel time entirely. You can attend from the comfort of your own home in Strabane, at times that fit your schedule, without the burden of a 2.5-3 hour round trip. For border communities that have historically faced barriers accessing services, online therapy represents a genuine solution to improving mental health accessibility.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Serving Border Communities Section */}
      <section className="py-16 sm:py-20 bg-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Supporting Strabane's Community
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Strabane is a close-knit border town with its own unique character and strong community spirit. As a border community, Strabane residents have unique experiences that shape daily life. I provide respectful, non-judgmental support for all backgrounds and circumstances. My approach is completely neutral and professional - I'm here to support your mental health and wellbeing, regardless of where you come from or what your situation is.
              </p>
              <p>
                Whether you're dealing with everyday stresses like work pressure, financial worries, or relationship challenges, or facing more complex issues such as anxiety, chronic pain, or economic stress, my approach is always respectful, confidential, and tailored to your individual needs. I understand that for many working-class communities, accessing mental health support can feel intimidating or unaffordable. My pricing is transparent and accessible, and I'm committed to providing professional care without judgment or pretension.
              </p>
              <p>
                For Strabane residents, the benefit of online sessions extends beyond convenience. Many people in border areas face long distances to access specialist services, creating real barriers to consistent care. Online therapy removes these barriers entirely. You don't need to arrange childcare, take time off work, or factor in fuel costs and travel time. You can schedule sessions during your lunch break, in the evening after work, or on weekends - whatever fits your life best.
              </p>
              <p>
                I work collaboratively with local GPs and healthcare providers to ensure you receive comprehensive support. For complex mental health needs, I can coordinate with your doctor to provide integrated care that complements medical treatment. My nursing background means I understand how physical and mental health are interconnected, and I'm comfortable working within the healthcare system to support your wellbeing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How I Can Support You
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-primary-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Shield className="w-8 h-8 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Anxiety and Stress Management
                    </h3>
                    <p className="text-gray-700">
                      Whether you're experiencing general anxiety, social anxiety, panic attacks, or stress related to work, finances, or daily life, hypnotherapy can help you develop calm, control, and confidence. I use evidence-based techniques to retrain your nervous system's response to stressors. Research shows hypnotherapy can reduce anxiety symptoms by 60%.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Heart className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Chronic Pain Management
                    </h3>
                    <p className="text-gray-700">
                      Hypnotherapy is highly effective for managing chronic pain conditions including back pain, fibromyalgia, arthritis, migraines, and neuropathic pain. Research shows it can reduce pain intensity by 29% on average, offering relief alongside medical treatment. My nursing background means I understand complex pain conditions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Users className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Economic Stress and Life Challenges
                    </h3>
                    <p className="text-gray-700">
                      Financial worries, job insecurity, and economic pressures affect mental health significantly. Hypnotherapy can help you manage anxiety related to money stress, build resilience during difficult times, and develop healthier coping strategies. My approach is practical, non-judgmental, and focused on what you can control.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-8 h-8 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Habit Breaking and Life Changes
                    </h3>
                    <p className="text-gray-700">
                      Whether you want to quit smoking, manage your weight, overcome unwanted habits, or build healthier patterns, hypnotherapy addresses the subconscious drivers behind behaviors. This makes lasting change easier than willpower alone. Particularly valuable for breaking cycles that cost money (smoking, emotional spending).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <h3 className="text-2xl font-heading font-semibold text-gray-900 mt-8 mb-4">
                Accessible Mental Health Care
              </h3>
              <p>
                Mental health support should be accessible to everyone, regardless of income or background. For ongoing support, I offer a discounted 4-session package. There are no hidden fees, no pressure to commit to long-term contracts, and you can stop at any time. I provide receipts for insurance claims if your policy covers complementary therapies.
              </p>
              <p>
                Online therapy is particularly cost-effective for Strabane residents. You save on fuel costs and time off work. A 60-minute online session costs the same as in-person but doesn't require hours of travel or additional travel expenses. This makes regular therapy much more sustainable financially and practically.
              </p>

              <h3 className="text-2xl font-heading font-semibold text-gray-900 mt-8 mb-4">
                Evidence-Based Treatment You Can Trust
              </h3>
              <p>
                I'm registered with the Complementary and Natural Healthcare Council (CNHC), the UK's government-backed regulator for complementary therapies. This registration requires 450+ hours of professional training, ongoing CPD, professional indemnity insurance, and adherence to strict ethical guidelines. My 15 years as a registered nurse at Belfast City Hospital provides additional medical context and understanding of complex health conditions.
              </p>
              <p>
                Hypnotherapy isn't magic or mind control - it's an evidence-based therapeutic technique that works with your brain's natural neuroplasticity. Research shows it's effective for anxiety (60% reduction in symptoms), chronic pain management (29% pain reduction), and habit change when the client is motivated. I combine hypnotherapy with cognitive behavioral techniques, solution-focused therapy, and trauma-informed approaches for comprehensive support.
              </p>

              <h3 className="text-2xl font-heading font-semibold text-gray-900 mt-8 mb-4">
                Working with Border Communities
              </h3>
              <p>
                As a border town, Strabane has unique characteristics that shape community life. I understand that border communities navigate particular challenges and experiences. My approach is completely neutral, respectful, and non-political. I'm here to support your mental health and wellbeing, regardless of your background, identity, or circumstances. You'll receive the same professional, confidential care as any other client.
              </p>
              <p>
                Some border residents work across the boundary, shop in different currencies, or have family connections on both sides. These practicalities can create stress around Brexit changes, customs, healthcare access, or economic uncertainty. Hypnotherapy can help you manage the anxiety that comes with these situations, building resilience and calm in the face of circumstances beyond your control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Resources Section */}
      <section className="py-16 sm:py-20 bg-red-50 border-l-4 border-red-600">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
              Crisis Support for Strabane Residents
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              If you're experiencing a mental health crisis, self-harm thoughts, or suicidal ideation, please reach out for immediate support. Hypnotherapy is not appropriate for crisis situations - you need urgent professional help.
            </p>
            <div className="bg-white rounded-xl p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Western Health and Social Care Trust Crisis Response Team</h3>
                <p className="text-gray-700 mb-1">24/7 mental health crisis support for West Tyrone including Strabane</p>
                <a href="tel:02882835252" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  028 8283 5252
                </a>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Lifeline NI</h3>
                <p className="text-gray-700 mb-1">24/7 crisis helpline for anyone in distress or despair</p>
                <a href="tel:08088088000" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  0808 808 8000
                </a>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Samaritans</h3>
                <p className="text-gray-700 mb-1">24/7 emotional support for anyone struggling to cope</p>
                <a href="tel:116123" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  116 123
                </a>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <p className="text-gray-700">
                  <strong>In an emergency:</strong> Call 999 or go to the emergency department at Altnagelvin Area Hospital, Derry.
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>GP referral:</strong> For complex mental health needs including diagnosed conditions (depression, PTSD, bipolar disorder, schizophrenia, psychosis), please speak to your GP. Hypnotherapy can support mental wellness but is not a replacement for psychiatric care.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Attending Sessions from Strabane
            </h2>

            <div className="bg-accent-100 border-l-4 border-accent-600 rounded-r-xl p-6 mb-8">
              <div className="flex items-start space-x-4">
                <Monitor className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                    Online Sessions: The Practical Choice
                  </h3>
                  <p className="text-gray-700 mb-4">
                    For Strabane clients, we <strong>strongly recommend online sessions</strong> rather than traveling to our physical clinics. Here's why:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Distance:</strong> Approximately 80 miles to the Belfast clinic (around 1 hr 30 min) - that's 3 hours of travel for a 60-minute session</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Cost:</strong> 110+ mile round trip means the cost of fuel, plus wear on your vehicle</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Time:</strong> 3+ hours total commitment (travel + session) vs 60 minutes at home</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Convenience:</strong> No childcare arrangements, no time off work, sessions fit around your life</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Consistency:</strong> Easier to commit to regular sessions when travel isn't a barrier</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Equally effective:</strong> Research shows online hypnotherapy produces the same outcomes as in-person sessions</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Affordable:</strong> Save the cost of fuel every session, making therapy more sustainable long-term</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 mt-4">
                    Online sessions use secure, GDPR-compliant video calling (Zoom or similar). You'll need a private space, stable internet connection, and a device with a camera (laptop, tablet, or smartphone). Sessions are conducted exactly as in-person appointments, with the same professionalism, privacy, and therapeutic effectiveness.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-100 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <Car className="w-8 h-8 text-gray-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                    In-Person Sessions (Available but Not Recommended)
                  </h3>
                  <p className="text-gray-700 mb-4">
                    If you prefer in-person sessions despite the distance, two clinic locations are available: the Belfast clinic on the Ormeau Road and the Lisburn clinic on Antrim Street, both with days and times that vary week to week. However, online sessions are strongly recommended for Strabane residents due to the travel involved.
                  </p>
                  <div className="space-y-3 text-gray-700">
                    <div>
                      <p className="font-semibold">To Belfast Clinic (Belfast Wellness Clinic, 228 Ormeau Road, BT7 2FZ):</p>
                      <p>From Strabane: approximately 80 miles via the A5 south to Omagh, then the M1 east into Belfast and south along the Ormeau Road (A24). Around 1 hour 30 minutes. The clinic sits in south Belfast, roughly 10 minutes from the city centre. If you travel by public transport, Botanic and City Hospital are the closest rail stations, with Lanyon Place serving regional routes, and Metro buses run along the Ormeau Road corridor. Clinic days and times vary - current availability and online booking are on the{' '}
                        <a
                          href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent-600 hover:text-accent-700 font-medium underline"
                        >
                          clinic booking page
                        </a>
                        , and early evening appointments are available.</p>
                    </div>
                    <div>
                      <p className="font-semibold">To Lisburn Clinic (The Arzen Collective, 13B Antrim Street, BT28):</p>
                      <p>From Strabane: the A5 south to Omagh, then the M1 east, leaving at the Lisburn junctions — a shorter run than continuing into Belfast. Clinic days and times vary, and early evening appointments are available.</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mt-4 italic">
                    Honest advice: For weekly or fortnightly sessions over several weeks, the travel commitment from Strabane isn't sustainable financially or practically for most people. Online sessions offer the same quality of care with none of the travel burden or expense.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Sessions and Packages
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">Initial Consultation</h3>
                <p className="text-gray-600">90 minutes including assessment and first session</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">Single Session</h3>
                <p className="text-gray-600">60-minute follow-up session</p>
              </div>
              <div className="bg-accent-100 rounded-xl p-6 text-center border-2 border-accent-600">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">4-Session Package</h3>
                <p className="text-gray-600">Best value for ongoing work, at a discount on single sessions</p>
              </div>
            </div>
            <div className="bg-primary-50 rounded-xl p-6 text-center">
              <p className="text-gray-700 mb-2">
                <strong>Online sessions save you money:</strong> you avoid the fuel costs of every round trip, plus the wear on your vehicle.
              </p>
              <p className="text-gray-700">
                Over a multi-session package, that adds up to a significant saving in travel costs alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Common Questions from Strabane Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you serve border communities?
                </h3>
                <p className="text-gray-700">
                  Absolutely. I work with clients from border communities across Northern Ireland and beyond. My approach is completely neutral, non-political, and respectful of all backgrounds and circumstances. You'll receive the same professional, confidential care regardless of where you're from or what your situation is. Border towns like Strabane have unique characteristics, and I understand the importance of providing non-judgmental support.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are online sessions secure and private?
                </h3>
                <p className="text-gray-700">
                  Yes. I use GDPR-compliant, encrypted video calling platforms (Zoom or similar) for all online sessions. Your data is protected, recordings are never made without explicit consent, and all conversations are confidential under professional ethics guidelines. You'll need a private space for your sessions (bedroom, office, or anywhere you won't be interrupted), but the technology itself is completely secure.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with economic stress and anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes. While hypnotherapy can't change your financial situation directly, it can significantly help you manage the anxiety, stress, and overwhelm that comes with economic pressures. I teach techniques to calm your nervous system, reduce worry spirals, improve sleep, and build resilience. Many clients find they make better decisions and cope more effectively when they're not consumed by anxiety. The approach is practical, non-judgmental, and focused on what you can control.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is hypnotherapy affordable for working-class families?
                </h3>
                <p className="text-gray-700">
                  I aim to make therapy as accessible as possible, with a discounted 4-session package for ongoing work. There's no pressure to commit to multiple sessions - you can do one at a time. With online sessions, you also save the cost of every round trip from Strabane. Some private health insurance policies cover complementary therapies - check your policy. I provide receipts for insurance claims.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How effective are online sessions compared to in-person?
                </h3>
                <p className="text-gray-700">
                  Research shows online hypnotherapy produces the same therapeutic outcomes as face-to-face sessions. The hypnotic trance state is just as deep, the techniques are identical, and the therapeutic relationship develops equally well. Many clients actually find it easier to relax at home in their own familiar environment. The only requirement is a private space, stable internet connection, and a device with a camera.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What can hypnotherapy help with?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is effective for anxiety (general anxiety, panic attacks, social anxiety), stress management, chronic pain (back pain, fibromyalgia, arthritis, migraines), habit breaking (smoking, nail biting, emotional eating), weight management, sleep problems, phobias, and building confidence. It works by accessing your subconscious mind to change unhelpful patterns and build new, healthier responses.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is hypnotherapy safe? Will I lose control?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is completely safe and you remain in control throughout. Hypnosis is a natural state of focused attention - similar to being absorbed in a book or film - not unconsciousness or sleep. You can't be made to do anything against your will, you'll remember everything, and you can open your eyes and end the session at any time. It's a collaborative process where you're an active participant in your own healing.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  This varies depending on your goals and circumstances. Some clients achieve their goals in 3-4 sessions (habit change, specific phobias), while others benefit from 6-8 sessions for more complex issues (chronic anxiety, pain management). We'll discuss realistic expectations in your initial consultation and review progress regularly. There's no pressure to commit to multiple sessions - you can decide after each appointment whether to continue.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can I see you if I'm taking medication?
                </h3>
                <p className="text-gray-700">
                  Yes. Hypnotherapy works alongside medication (SSRIs, SNRIs, benzodiazepines) without any negative interactions. In fact, research shows that combining hypnotherapy with medication and/or talking therapy produces better outcomes than any single treatment alone. Never stop or change medication without consulting your GP. I can work collaboratively with your doctor to provide integrated care.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How do I book an appointment?
                </h3>
                <p className="text-gray-700">
                  You can book online via my Calendly link (click "Book Free Consultation" above) or call me directly on 07932 139245. The initial consultation is 90 minutes and includes a detailed assessment, discussion of your goals, explanation of how hypnotherapy works, and your first session. There's no obligation to continue after the initial consultation - you can decide if it's right for you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              What County Tyrone Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I was worried about cost but Sharon's pricing was transparent and fair. Online sessions meant I didn't have to spend money on petrol or take time off work. My anxiety has improved so much and I'm sleeping better. Best decision I made."
                </p>
                <p className="text-gray-900 font-semibold">- C.O., West Tyrone</p>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "Sharon never made me feel judged for my situation. Her approach was professional but down-to-earth. The hypnotherapy helped me manage chronic back pain that was affecting my work. Online sessions were so convenient."
                </p>
                <p className="text-gray-900 font-semibold">- R.M., County Tyrone</p>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I appreciated the neutral, respectful approach. No politics, no assumptions, just professional support. The online format was perfect - I could fit sessions around my shift work. My stress levels have dropped significantly."
                </p>
                <p className="text-gray-900 font-semibold">- D.L., West Tyrone</p>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "Quit smoking after 25 years with Sharon's help. Four sessions and I haven't touched a cigarette in 10 months. Saving hundreds of pounds a month now. Online appointments meant no excuses about travel. Highly recommend."
                </p>
                <p className="text-gray-900 font-semibold">- K.B., County Tyrone</p>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I was dealing with anxiety about money and work stress. Sharon's practical approach really helped me cope better and stop the worry spirals. The online sessions were easy to fit around childcare. Feel much calmer now."
                </p>
                <p className="text-gray-900 font-semibold">- N.F., West Tyrone</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <CTA />
    </main>
  );
};

export default StrabaneHypnotherapy;
